// Minimal IndexedDB helper without external deps
// Provides simple CRUD for stores: users, accounts, categories, transactions, meta

export type StoreName =
  | 'users'
  | 'accounts'
  | 'categories'
  | 'transactions'
  | 'tasks'
  | 'recurring_transactions'
  | 'meta'
  | 'sync_queue'
  | 'okr_objectives'
  | 'okr_key_results'
  | 'okr_indicators'
  | 'investment_plans'
  | 'investment_scenarios'
  | 'investment_ledgers'
  | 'investment_records'
  | 'investment_allocations'
  | 'investment_goals';

const DB_NAME = 'prumo-offline';
const DB_VERSION = 4;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Criar stores se não existirem
      if (!db.objectStoreNames.contains('users')) {
        const s = db.createObjectStore('users', { keyPath: 'id', autoIncrement: true });
        s.createIndex('email', 'email', { unique: true });
      }
      if (!db.objectStoreNames.contains('accounts')) {
        const s = db.createObjectStore('accounts', { keyPath: 'id', autoIncrement: true });
        s.createIndex('name', 'name', { unique: false });
      }
      if (!db.objectStoreNames.contains('categories')) {
        const s = db.createObjectStore('categories', { keyPath: 'id', autoIncrement: true });
        s.createIndex('type', 'type', { unique: false });
      }
      if (!db.objectStoreNames.contains('transactions')) {
        const s = db.createObjectStore('transactions', { keyPath: 'id', autoIncrement: true });
        s.createIndex('competence', 'competence', { unique: false });
        s.createIndex('is_paid', 'is_paid', { unique: false });
        s.createIndex('due_date', 'due_date', { unique: false });
      }
      if (!db.objectStoreNames.contains('tasks')) {
        const s = db.createObjectStore('tasks', { keyPath: 'id', autoIncrement: true });
        s.createIndex('status', 'status', { unique: false });
        s.createIndex('due_date', 'due_date', { unique: false });
        s.createIndex('is_urgent', 'is_urgent', { unique: false });
        s.createIndex('is_important', 'is_important', { unique: false });
      }
      if (!db.objectStoreNames.contains('recurring_transactions')) {
        const s = db.createObjectStore('recurring_transactions', { keyPath: 'id', autoIncrement: true });
        s.createIndex('user_id', 'user_id', { unique: false });
        s.createIndex('is_active', 'is_active', { unique: false });
        s.createIndex('start_date', 'start_date', { unique: false });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains('sync_queue')) {
        const s = db.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
        s.createIndex('status', 'status', { unique: false });
      }
      if (!db.objectStoreNames.contains('okr_objectives')) {
        const s = db.createObjectStore('okr_objectives', { keyPath: 'id', autoIncrement: true });
        s.createIndex('status', 'status', { unique: false });
        s.createIndex('period_type', 'period_type', { unique: false });
      }
      if (!db.objectStoreNames.contains('okr_key_results')) {
        const s = db.createObjectStore('okr_key_results', { keyPath: 'id', autoIncrement: true });
        s.createIndex('objective_id', 'objective_id', { unique: false });
        s.createIndex('status', 'status', { unique: false });
      }
      if (!db.objectStoreNames.contains('okr_indicators')) {
        const s = db.createObjectStore('okr_indicators', { keyPath: 'id', autoIncrement: true });
        s.createIndex('key_result_id', 'key_result_id', { unique: false });
        s.createIndex('month', 'month', { unique: false });
      }
      if (!db.objectStoreNames.contains('investment_plans')) {
        const s = db.createObjectStore('investment_plans', { keyPath: 'id', autoIncrement: true });
        s.createIndex('name', 'name', { unique: false });
      }
      if (!db.objectStoreNames.contains('investment_scenarios')) {
        const s = db.createObjectStore('investment_scenarios', { keyPath: 'id', autoIncrement: true });
        s.createIndex('plan_id', 'plan_id', { unique: false });
      }
      if (!db.objectStoreNames.contains('investment_ledgers')) {
        const s = db.createObjectStore('investment_ledgers', { keyPath: 'id', autoIncrement: true });
        s.createIndex('currency', 'currency', { unique: false });
      }
      if (!db.objectStoreNames.contains('investment_records')) {
        const s = db.createObjectStore('investment_records', { keyPath: 'id', autoIncrement: true });
        s.createIndex('ledger_id', 'ledger_id', { unique: false });
        s.createIndex('reference_month', 'reference_month', { unique: false });
      }
      if (!db.objectStoreNames.contains('investment_allocations')) {
        db.createObjectStore('investment_allocations', { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains('investment_goals')) {
        const s = db.createObjectStore('investment_goals', { keyPath: 'id', autoIncrement: true });
        s.createIndex('plan_id', 'plan_id', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

function tx(store: StoreName, mode: IDBTransactionMode = 'readonly') {
  return openDb().then((db) => db.transaction(store, mode).objectStore(store));
}

export async function dbGetAll<T>(store: StoreName): Promise<T[]> {
  const s = await tx(store);
  return new Promise((resolve, reject) => {
    const req = s.getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });
}

export async function dbGetById<T>(store: StoreName, id: number): Promise<T | undefined> {
  const s = await tx(store);
  return new Promise((resolve, reject) => {
    const req = s.get(id);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

export async function dbAdd<T>(store: StoreName, value: T): Promise<T & { id: number }> {
  const s = await tx(store, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = s.add(value as any);
    req.onsuccess = () => resolve({ ...(value as any), id: req.result as number });
    req.onerror = () => reject(req.error);
  });
}

export async function dbPut<T>(store: StoreName, value: T & { id: number }): Promise<T> {
  const s = await tx(store, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = s.put(value as any);
    req.onsuccess = () => resolve(value);
    req.onerror = () => reject(req.error);
  });
}

export async function dbDelete(store: StoreName, id: number): Promise<void> {
  const s = await tx(store, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = s.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function dbClear(store: StoreName): Promise<void> {
  const s = await tx(store, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = s.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function dbSetMeta(key: string, value: any): Promise<void> {
  const s = await tx('meta', 'readwrite');
  return new Promise((resolve, reject) => {
    const req = s.put({ key, value });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function dbGetMeta<T>(key: string): Promise<T | undefined> {
  const s = await tx('meta', 'readonly');
  return new Promise((resolve, reject) => {
    const req = s.get(key);
    req.onsuccess = () => resolve(req.result?.value as T | undefined);
    req.onerror = () => reject(req.error);
  });
}


